import React, { FC, useEffect, useMemo, useState } from "react";

import { UpcomingSessionsProps } from "./Types";
import {
	UpcomingSessionRow,
	getUpcomingSessionsColumns,
} from "./utils/UpcomingSessionsColumns";
import { Translate } from "@/app/application/Translate";
import { TrainingSessionStatus } from "@/features/training/model/Training/TrainingSession";
import { StorageKeys } from "@/storage/storageKeys";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import {
	Box,
	Button,
	Chip,
	Skeleton,
	Stack,
	Tooltip,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import { DataGrid, GridRowParams } from "@mui/x-data-grid";
import dayjs from "dayjs";

import {
	useFetchCourseSessions,
	useFetchDrillSessions,
} from "@/service/TrainingQueries";
import { ReadFromLocalStorage, WriteToLocalStorage } from "@/service/Utilities";

const DASHBOARD_GRID_HEIGHT = 292;

export const UpcomingSessionsDashboard: FC<UpcomingSessionsProps> = ({
	type,
	data,
	name,
}) => {
	const theme = useTheme();
	const queryOptions = { Status: TrainingSessionStatus.Active };
	const isCourseSessionsActive = type === "course";

	const { data: courseSessions, isLoading: isLoadingCourses } =
		useFetchCourseSessions(queryOptions, isCourseSessionsActive);
	const { data: drillSessions, isLoading: isLoadingDrills } =
		useFetchDrillSessions(queryOptions, !isCourseSessionsActive);

	const sessions = isCourseSessionsActive ? courseSessions : drillSessions;
	const isLoading = isLoadingCourses || isLoadingDrills;

	type SortOrder = "asc" | "desc";
	const storageKey = useMemo(
		() => StorageKeys.upcomingSessionsSortOrder(type, data),
		[type, data],
	);
	const [sortOrder, setSortOrder] = useState<SortOrder>(() => {
		if (typeof window === "undefined") return "asc";
		const saved = ReadFromLocalStorage<string>(storageKey, "asc");
		return saved === "desc" || saved === "asc" ? saved : "asc";
	});

	useEffect(() => {
		const saved = ReadFromLocalStorage<string>(storageKey, "asc");
		if (saved === "asc" || saved === "desc") setSortOrder(saved as SortOrder);
	}, [storageKey]);

	useEffect(() => {
		WriteToLocalStorage(storageKey, sortOrder);
	}, [sortOrder, storageKey]);

	const toggleSort = () => setSortOrder((p) => (p === "asc" ? "desc" : "asc"));

	const filteredSessions = useMemo(() => {
		if (!Array.isArray(sessions)) return [];
		return sessions.filter((session) =>
			data === "upcoming"
				? dayjs(session.Date).isAfter(dayjs())
				: dayjs(session.Date).isBefore(dayjs()),
		);
	}, [sessions, data]);

	const sortedSessions = useMemo(() => {
		const arr = [...filteredSessions];
		arr.sort((a, b) => {
			const da = dayjs(a.Date).valueOf();
			const db = dayjs(b.Date).valueOf();
			return sortOrder === "asc" ? da - db : db - da;
		});
		return arr;
	}, [filteredSessions, sortOrder]);

	const handleNavigate = (session: UpcomingSessionRow) => {
		if (typeof session.CourseId === "number") {
			window.open(
				`/training/courses/${session.CourseId}/sessions/${session.Id}`,
				"_blank",
				"noopener,noreferrer",
			);
			return;
		}

		if (typeof session.DrillId === "number") {
			window.open(
				`/training/drills/${session.DrillId}/sessions/${session.Id}`,
				"_blank",
				"noopener,noreferrer",
			);
		}
	};

	const columns = useMemo(
		() => getUpcomingSessionsColumns(handleNavigate),
		[handleNavigate],
	);

	const hasSessions = sortedSessions.length > 0;

	return (
		<Box
			sx={{ display: "flex", flexDirection: "column", minHeight: "100%" }}
		>
			{/* Header strip – similar to archive cards */}
			<Box
				sx={(t) => ({
					py: 1.15,
					px: 1.6,
					display: "flex",
					flexWrap: "wrap",
					alignItems: "flex-start",
					justifyContent: "space-between",
					gap: 1,
					bgcolor:
						t.palette.mode === "dark"
							? alpha(t.palette.primary.main, 0.12)
							: alpha(t.palette.primary.main, 0.05),
					borderBottom: 1,
					borderColor: "divider",
					borderRadius: "14px 14px 0 0",
				})}
			>
				<Box sx={{ minWidth: 0 }}>
					<Typography
						variant="overline"
						sx={{
							display: "block",
							color: "primary.main",
							fontWeight: 700,
							letterSpacing: "0.08em",
							textTransform: "uppercase",
						}}
					>
						<Translate String="Auto.Features.Training.Pages.Dashboard.UpcomingSessionsDashboard.L152C7.Schedule">Schedule</Translate>
					</Typography>
					<Typography
						variant="subtitle2"
						sx={{ fontWeight: 700, lineHeight: 1.15 }}
					>
						{data === "upcoming" ? (
							<>
								<Translate String="Training.Dashboard.Upcoming">
									Upcoming
								</Translate>{" "}
								{name}{" "}
								<Translate String="Training.Dashboard.Sessions">
									Sessions
								</Translate>
							</>
						) : (
							<>
								<Translate String="Training.Dashboard.Overdue">
									Overdue
								</Translate>{" "}
								{name}{" "}
								<Translate String="Training.Dashboard.Sessions">
									Sessions
								</Translate>
							</>
						)}
					</Typography>
					<Typography
						variant="caption"
						color="text.secondary"
						sx={{ display: "block", mt: 0.25 }}
					>
						{hasSessions ? (
							<>
								{sortedSessions.length}{" "}
								<Translate String="Training.Dashboard.SessionsLabel">
									sessions
								</Translate>
							</>
						) : (
							<Translate String="Training.Dashboard.NoSessions">
								No sessions
							</Translate>
						)}
					</Typography>
				</Box>

				<Stack
					direction="row"
					spacing={1}
					alignItems="center"
					useFlexGap
					flexWrap="wrap"
					sx={{ ml: "auto", justifyContent: "flex-end" }}
				>
					<Chip
						size="small"
						label={name}
						sx={{
							height: 22,
							borderRadius: 2,
							bgcolor: alpha(theme.palette.primary.main, 0.08),
							color: "primary.main",
							fontWeight: 700,
						}}
					/>
					<Tooltip
						title={
							sortOrder === "asc"
								? "Oldest → Newest (click to switch)"
								: "Newest → Oldest (click to switch)"
						}
					>
						<span>
							<Button
								variant="outlined"
								size="small"
								onClick={toggleSort}
								disabled={!hasSessions}
								startIcon={
									sortOrder === "asc" ? (
										<ArrowUpwardIcon />
									) : (
										<ArrowDownwardIcon />
									)
								}
								sx={{
									textTransform: "none",
									borderRadius: 2,
									fontWeight: 700,
									px: 1.25,
								}}
							>
								{sortOrder === "asc" ? "Oldest" : "Newest"}
							</Button>
						</span>
					</Tooltip>
				</Stack>
			</Box>

			{/* Body */}
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					pt: 1.1,
					px: 0.4,
					pb: 0.25,
					backgroundColor:
						theme.palette.mode === "dark"
							? alpha(theme.palette.common.white, 0.02)
							: alpha(theme.palette.primary.main, 0.015),
				}}
			>
				{isLoading ? (
					<Box sx={{ pt: 1 }}>
						{[1, 2, 3, 4, 5].map((i) => (
							<Skeleton
								key={i}
								variant="rectangular"
								height={40}
								sx={{ mb: 1, borderRadius: 1.5 }}
							/>
						))}
					</Box>
				) : !hasSessions ? (
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							py: 4,
							px: 2.5,
							textAlign: "center",
							borderRadius: 2,
							border: `1px dashed ${alpha(theme.palette.divider, 0.8)}`,
							bgcolor:
								theme.palette.mode === "dark"
									? alpha(theme.palette.common.white, 0.02)
									: alpha(theme.palette.primary.main, 0.02),
							mx: 0.8,
						}}
					>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
							{data === "upcoming" ? (
								<Translate String="Training.Dashboard.NoUpcomingSessions">
									No upcoming sessions
								</Translate>
							) : (
								<Translate String="Training.Dashboard.NoOverdueSessions">
									No overdue sessions
								</Translate>
							)}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							<Translate String="Training.Dashboard.SessionsEmptyHint">
								New items will appear here when scheduled.
							</Translate>
						</Typography>
					</Box>
				) : (
					<Box sx={{ height: DASHBOARD_GRID_HEIGHT, minHeight: DASHBOARD_GRID_HEIGHT }}>
						<DataGrid<UpcomingSessionRow>
							rows={sortedSessions}
							columns={columns}
							getRowId={(row) => row.Id}
							disableColumnFilter
							disableDensitySelector
							disableColumnSelector
							pageSizeOptions={[5, 10, 25]}
							initialState={{
								pagination: { paginationModel: { pageSize: 5, page: 0 } },
							}}
							onRowClick={(params: GridRowParams<UpcomingSessionRow>) =>
								handleNavigate(params.row)
							}
							sx={{
								height: "100%",
								border: "none",
								fontSize: theme.typography.body2.fontSize,
								"& .MuiDataGrid-columnHeaders": { display: "none" },
								"& .MuiDataGrid-cell": {
									borderBottom: "none",
									py: 0.5,
								},
								"& .MuiDataGrid-row": {
									borderRadius: 1.5,
									"&:nth-of-type(even)": {
										bgcolor: alpha(theme.palette.primary.main, 0.02),
									},
									"&:hover": {
										cursor: "pointer",
										backgroundColor:
											theme.palette.mode === "light"
												? alpha(theme.palette.primary.main, 0.06)
												: alpha(theme.palette.primary.main, 0.18),
									},
								},
								"& .MuiDataGrid-virtualScroller": {
									overflowX: "hidden",
								},
								"& .MuiDataGrid-footerContainer": {
									borderTop: `1px solid ${alpha(theme.palette.divider, 0.75)}`,
									minHeight: 40,
									justifyContent: "flex-end",
									px: 0.5,
								},
							}}
						/>
					</Box>
				)}
			</Box>
		</Box>
	);
};
